import React, { Suspense, useEffect } from 'react'
import {HashRouter, Navigate, Route, Routes, useNavigate} from 'react-router-dom'
import './scss/style.scss'
import { useAuth } from './auth/useAuth'
import ErrorBoundary from './components/ErrorBoundary'
import { subscribeNavigationRequest } from './tool/navigationEvents'
import { sendFrontendEvent } from './tool/telemetry'

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
)

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Pages
const Login = React.lazy(() => import('./views/pages/login/Login'))
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'))
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'))

const NavigationEventListener = () => {
  const navigate = useNavigate()

  useEffect(() => {
    return subscribeNavigationRequest((path) => navigate(path))
  }, [navigate])

  return null
}

const App = () => {
  useEffect(() => {
    const onError = (event) => {
      const message = event?.message || 'Unhandled frontend error'
      sendFrontendEvent({
        level: 'error',
        event: 'window_error',
        message,
        metadata: {
          source: event?.filename || '',
          line: event?.lineno || 0,
          column: event?.colno || 0,
        },
      })
    }

    const onUnhandledRejection = (event) => {
      const reason = event?.reason
      const message = typeof reason === 'string'
        ? reason
        : (reason?.message || 'Unhandled promise rejection')
      sendFrontendEvent({
        level: 'error',
        event: 'unhandled_rejection',
        message,
      })
    }

    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onUnhandledRejection)

    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
    }
  }, [])

  const auth = useAuth()
  const targetPage = auth.isLoggedIn
    ? <Route path="*" name="Home" element={<DefaultLayout />} />
    : <Route path="*" element={<Navigate to="login" replace />} />

  return (
    <HashRouter>
      <NavigationEventListener />
      <ErrorBoundary>
        <Suspense fallback={loading}>
          <Routes>
            <Route exact path="/login" name="Login Page" element={<Login />} />
            <Route exact path="/404" name="Page 404" element={<Page404 />} />
            <Route exact path="/500" name="Page 500" element={<Page500 />} />
            {targetPage}
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </HashRouter>
  )
}

export default App
